import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { motion, useSpring } from 'framer-motion';
import { useDrag } from 'react-use-gesture';
import { useTranslations } from 'next-intl';

import { FloorPlanContext } from '../../floorPlanProvider';
import { myFuturiumMap } from '../../../../../helper';

import FloorPlanOpenIcon from '../../svgs/icons/floorPlanOpen';
import Headline from '../../../../atoms/headline/headline';
import Copy from '../../../../atoms/copy/copy';
import LongArrowRightIcon from '../../../../icons/longArrowRight.svg';
import LongArrowLeftIcon from '../../../../icons/longArrowLeft.svg';

import PlanTechnic from '../../svgs/shapes/planTechnic';
import PlanHuman from '../../svgs/shapes/planHuman';
import PlanNature from '../../svgs/shapes/planNature';

import Marker from '../marker/marker';

import StyledPlan from './plan.style';

const plans = {
  technic: <PlanTechnic className="plan__svg" />,
  human: <PlanHuman className="plan__svg" />,
  nature: <PlanNature className="plan__svg" />
};

const bounds = {
  technic: { top: -675, bottom: 675, right: 1257, left: -1257 },
  human: { top: -887, bottom: 887, right: 828, left: -828 },
  nature: { top: -741, bottom: 741, right: 868, left: -868 }
};

const Plan = ({ label, markers, variant }) => {
  const { activeMarker, expanded, expandedPlan, hoverPlan, hoveredPlan, expandPlan, collapsePlan } =
    useContext(FloorPlanContext);
  const t = useTranslations();
  const x = useSpring(0, { stiffness: 1000, damping: 100 });
  const y = useSpring(0, { stiffness: 1000, damping: 100 });

  const drag = useDrag(
    ({ offset }) => {
      x.set(offset[0]);
      y.set(offset[1]);
    },
    {
      enabled: expanded,
      bounds: bounds[variant],
      rubberband: 0.15,
      filterTaps: true
    }
  );

  return (
    <StyledPlan
      variant={variant}
      expanded={expandedPlan === variant}
      hovered={hoveredPlan === variant}
    >
      <div className="plan__content">
        <div className="plan__ratio">
          <motion.div
            className="plan__detail"
            {...drag()}
            style={{
              x,
              y
            }}
          >
            {plans[variant]}
            {markers.map(marker => (
              <Marker className="plan__marker" {...marker} key={marker.id} variant={variant} />
            ))}
          </motion.div>
        </div>

        {!expanded && (
          <button
            className="plan__button"
            type="button"
            disabled={expanded}
            onClick={() => expandPlan(variant)}
            onMouseEnter={() => hoverPlan(variant)}
            onMouseLeave={() => hoverPlan('')}
            onFocus={() => hoverPlan(variant)}
            onBlur={() => hoverPlan('')}
            aria-label={`${t('floorPlan.openPlan')} ${label}`}
          >
            {activeMarker &&
              activeMarker.plan &&
              myFuturiumMap(activeMarker.plan, 'name') === variant && (
                <div className="plan__active-marker">
                  <FloorPlanOpenIcon />
                  <Copy thick>{t('floorPlan.currentLabel')}</Copy>
                  <Copy>{activeMarker.headline}</Copy>
                </div>
              )}
            <Headline tertiary>{label}</Headline>
            <LongArrowRightIcon className="plan__button-svg" />
          </button>
        )}
      </div>
      {expandedPlan === variant && (
        <div className="plan__meta">
          <button
            className="plan__back-button"
            type="button"
            onClick={() => {
              x.set(0);
              y.set(0);
              collapsePlan();
            }}
          >
            <LongArrowLeftIcon />
            <Headline tag="span" subline>
              {t('floorPlan.backToOverview')}
            </Headline>
          </button>
          <Headline className="plan__headline" secondary>
            {label}
          </Headline>
        </div>
      )}
    </StyledPlan>
  );
};
Plan.propTypes = {
  label: PropTypes.string.isRequired,
  markers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  variant: PropTypes.string.isRequired
};

export default Plan;
